import { TemplateType } from '@/types/boot';
import { TemplateConfigurations, type TemplateFeatures } from '@/constants/templateConfig';
import { EmbeddedView } from '@/templateViews/EmbeddedView';
import { pickFirstNonExpressPaymentMethod } from '@/utils/paymentMethodResolvers';
import {
  CheckoutTemplate,
  type PaymentMethodSelectionContext,
  type TemplateInitContext,
  type TemplateView,
} from './CheckoutTemplate';

/**
 * Mobile-only embedded checkout (iframe-hosted).
 *
 * Behaviourally a DEFAULT clone — every available payment method, the same
 * express row and the same default-method resolution. The difference is
 * purely presentational and lives in {@link EmbeddedView}: DEFAULT's
 * `TopWrapper` (publisher hero + offer header + products list) is replaced by
 * a compact gray `TotalSummaryCard`.
 *
 * Because there is no eligibility requirement beyond the viewport (no wallet
 * to probe, no BE-supplied method to validate), the only soft-fail is
 * desktop, which silently falls back to DEFAULT via the registry.
 */
export class EmbeddedViewTemplate extends CheckoutTemplate {
  readonly type: TemplateType = TemplateType.EMBEDDED_VIEW;
  readonly View: TemplateView = EmbeddedView;
  readonly features: TemplateFeatures = TemplateConfigurations[TemplateType.EMBEDDED_VIEW];

  /**
   * Mobile-first, and also runs on a phone in landscape — the single-column
   * summary + payment stack just scrolls in the shorter viewport. True desktop
   * (>=1024 or any viewport outside the landscape phone band) falls back to
   * DEFAULT: `isLandscape()` caps at 1023×500.
   */
  isEligible(ctx: TemplateInitContext): boolean {
    return ctx.isMobile || ctx.isLandscape;
  }

  resolveDefaultPaymentMethod({ paymentMethods }: PaymentMethodSelectionContext) {
    return pickFirstNonExpressPaymentMethod(paymentMethods);
  }
}
